import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PhotoIcon, VideoCameraIcon, MusicalNoteIcon } from '@heroicons/react/24/outline';
import { IVideoClip, VideoEditConfig } from '@packages/global';

interface GenerationProgressProps {
  videoClips: IVideoClip[];
  editConfig?: VideoEditConfig | null;
  className?: string;
}

interface ProgressStats {
  total: number;
  imageCompleted: number;
  audioCompleted: number;
  videoCompleted: number;
}

export default function GenerationProgress({ videoClips, editConfig, className = '' }: GenerationProgressProps) {
  const { t } = useTranslation(['editor', 'common']);
  const stats = useMemo<ProgressStats>(() => {
    // 只统计轨道上的分镜
    const trackClipIds = new Set(editConfig?.clips?.map(c => c.clipId) || []);
    const trackClips = trackClipIds.size > 0
      ? videoClips.filter(clip => trackClipIds.has(clip.id))
      : videoClips;

    const total = trackClips.length;
    const imageCompleted = trackClips.filter(clip => clip.startImage || clip.endImage).length;
    const audioCompleted = trackClips.filter(clip => clip.scriptAudioUrl).length;
    const videoCompleted = trackClips.filter(clip => clip.videoUrl).length;

    return { total, imageCompleted, audioCompleted, videoCompleted };
  }, [videoClips, editConfig]);

  if (stats.total === 0) {
    return null;
  }

  const items = [
    {
      icon: PhotoIcon,
      label: t('editor:generationProgress.image'),
      completed: stats.imageCompleted,
      total: stats.total,
      color: 'text-green-400',
      bgColor: 'bg-green-500/20',
      progressColor: 'bg-green-500'
    },
    {
      icon: MusicalNoteIcon,
      label: t('editor:generationProgress.audio'),
      completed: stats.audioCompleted,
      total: stats.total,
      color: 'text-yellow-400',
      bgColor: 'bg-yellow-500/20',
      progressColor: 'bg-yellow-500'
    },
    {
      icon: VideoCameraIcon,
      label: t('editor:generationProgress.video'),
      completed: stats.videoCompleted,
      total: stats.total,
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/20',
      progressColor: 'bg-blue-500'
    }
  ];

  return (
    <div className={`flex flex-col gap-1.5 ${className}`}>
      {items.map((item) => {
        const Icon = item.icon;
        const percentage = Math.round((item.completed / item.total) * 100);
        const isComplete = item.completed === item.total;

        return (
          <div
            key={item.label}
            className="flex items-center gap-2"
            title={`${item.label}: ${item.completed}/${item.total} (${percentage}%)`}
          >
            {/* 图标 - 固定显示对应类型图标 */}
            <div className={`flex items-center justify-center w-5 h-5 rounded ${item.bgColor}`}>
              <Icon className={`w-3.5 h-3.5 ${isComplete ? item.color : 'text-gray-500'}`} />
            </div>
            {/* 横向进度条 + 数字 */}
            <div className="w-14 h-1.5 bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${item.progressColor} transition-all duration-300 ease-out`}
                style={{ width: `${percentage}%` }}
              />
            </div>
            <span className={`text-xs font-medium tabular-nums ${isComplete ? item.color : 'text-gray-500'}`}>
              {item.completed}/{item.total}
            </span>
          </div>
        );
      })}
    </div>
  );
}
